import React, { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronDown, HandHeart, Hourglass, Store, Wheat } from 'lucide-react';
import SlideWrapper from '../SlideWrapper';
import ParticleBackground from '../ParticleBackground';
import AnimatedCounter from '../AnimatedCounter';
import { useLanguage } from '../../context/LanguageContext';

const THEME = {
  label: 'Indigo Nomad Night',
  background: 'linear-gradient(180deg, #0f1220 0%, #1b2540 52%, #111726 100%)',
  particleColor: '#b88b59',
  particleCount: { mobile: 18, desktop: 42 },
  glowOverlay:
    'radial-gradient(circle at 14% 20%, rgba(104, 121, 180, 0.28), transparent 34%), radial-gradient(circle at 86% 78%, rgba(193, 139, 84, 0.32), transparent 44%)',
  textureOverlay:
    'repeating-linear-gradient(35deg, rgba(197, 173, 132, 0.06) 0 2px, transparent 2px 15px), repeating-linear-gradient(-35deg, rgba(37, 53, 88, 0.2) 0 2px, transparent 2px 17px)',
  textureOpacity: 0.3,
  vignetteOverlay: 'radial-gradient(circle, transparent 44%, rgba(5, 7, 12, 0.48) 100%)',
  text: '#ece2d0',
  skipBg: '#50608f',
  skipText: '#f8f2e6',
  badgeBg: 'rgba(35, 45, 71, 0.78)',
  badgeBorder: 'rgba(137, 158, 210, 0.4)',
  badgeIcon: '#d3b183',
  badgeText: '#eeddbf',
  badgeMuted: '#d0c0a3',
  kicker: '#bfcde8',
  kickerLine: '#6f89be',
  headingGradient: 'linear-gradient(90deg, #e7dcbd 0%, #9bb1de 50%, #bf8a55 100%)',
  headingText: '#f8f0dc',
  bodyText: '#d9d8e4',
  accentPositive: '#d5b98f',
  accentNegative: '#d59a7f',
  quoteText: '#b9bed2',
  primaryBg: 'linear-gradient(90deg, #47588a 0%, #5d74ad 52%, #be8c57 100%)',
  primaryBorder: 'rgba(191, 209, 245, 0.38)',
  primaryShadow: 'rgba(10, 16, 34, 0.65)',
  primaryText: '#fdf8ef',
  secondaryText: '#e4eaff',
  secondaryHover: '#f0f4ff',
  secondaryDecoration: '#9cb4e7',
  hintText: '#cad2ea',
  statBg: 'rgba(22, 31, 53, 0.78)',
  statBorder: 'rgba(103, 124, 176, 0.65)',
  statShadow: 'rgba(2, 5, 14, 0.4)',
  statIcon1: '#cfb184',
  statIcon2: '#9fb5df',
  statIcon3: '#d79a80',
  statNum1: '#ecd1a8',
  statNum2: '#c5d4ef',
  statNum3: '#e4aa92',
  statText: '#d9dff2',
  metaText: '#c3cbdf',
  focusRing: '#dde8ff',
  focusOffset: '#0f1220',
  chevron: '#c6d5f0',
  networkLine: 'rgba(162, 184, 236, 0.3)',
  networkLineStrong: 'rgba(201, 170, 131, 0.44)',
  networkNode: 'rgba(214, 227, 255, 0.62)',
  networkNodeMajor: 'rgba(229, 204, 171, 0.76)',
  networkNodeHub: 'rgba(248, 223, 188, 0.95)',
  networkPulse: 'rgba(171, 196, 255, 0.2)',
  networkLabel: 'rgba(215, 226, 248, 0.34)',
  networkFlowTrail: 'rgba(183, 203, 245, 0.24)',
  networkFlowWarm: 'rgba(242, 204, 148, 0.95)',
  networkFlowCool: 'rgba(194, 220, 255, 0.95)',
  networkFlowSpark: 'rgba(255, 241, 220, 0.9)',
};

const COLLABORATION_NODES = [
  { id: 'n1', x: 128, y: 168, tier: 'major' },
  { id: 'n2', x: 228, y: 140, tier: 'minor' },
  { id: 'n3', x: 308, y: 214, tier: 'minor' },
  { id: 'n4', x: 422, y: 182, tier: 'major' },
  { id: 'n5', x: 520, y: 236, tier: 'minor' },
  { id: 'n6', x: 700, y: 160, tier: 'major' },
  { id: 'n7', x: 784, y: 214, tier: 'minor' },
  { id: 'n8', x: 886, y: 170, tier: 'major' },
  { id: 'n9', x: 966, y: 232, tier: 'minor' },
  { id: 'n10', x: 1072, y: 206, tier: 'minor' },
  { id: 'n11', x: 182, y: 500, tier: 'major' },
  { id: 'n12', x: 302, y: 430, tier: 'minor' },
  { id: 'n13', x: 430, y: 468, tier: 'minor' },
  { id: 'n14', x: 530, y: 418, tier: 'major' },
  { id: 'n15', x: 652, y: 452, tier: 'minor' },
  { id: 'n16', x: 760, y: 500, tier: 'minor' },
  { id: 'n17', x: 876, y: 440, tier: 'major' },
  { id: 'n18', x: 984, y: 474, tier: 'minor' },
  { id: 'n19', x: 1088, y: 432, tier: 'minor' },
  { id: 'hubWest', x: 364, y: 320, tier: 'hub' },
  { id: 'hubCore', x: 600, y: 320, tier: 'hub' },
  { id: 'hubEast', x: 838, y: 314, tier: 'hub' },
];

const COLLABORATION_EDGES = [
  ['n1', 'n2'], ['n2', 'n3'], ['n3', 'n4'], ['n4', 'n5'],
  ['n6', 'n7'], ['n7', 'n8'], ['n8', 'n9'], ['n9', 'n10'],
  ['n11', 'n12'], ['n12', 'n13'], ['n13', 'n14'], ['n14', 'n15'],
  ['n15', 'n16'], ['n16', 'n17'], ['n17', 'n18'], ['n18', 'n19'],
  ['n3', 'hubWest'], ['n4', 'hubWest'], ['n4', 'hubCore'], ['n5', 'hubCore'],
  ['n6', 'hubCore'], ['n7', 'hubCore'], ['n8', 'hubEast'], ['n9', 'hubEast'],
  ['n12', 'hubWest'], ['n14', 'hubWest'], ['n15', 'hubCore'], ['n17', 'hubEast'],
  ['hubWest', 'hubCore'], ['hubCore', 'hubEast'],
  ['hubWest', 'hubEast'],
];

const COLLABORATION_NODE_LOOKUP = Object.fromEntries(
  COLLABORATION_NODES.map((node) => [node.id, node])
);

const COLLABORATION_NODE_RADIUS = {
  hub: 4.6,
  major: 3.1,
  minor: 2.2,
};

const COLLABORATION_RESOURCE_ROUTES = [
  {
    id: 'resourceRouteNorthArc',
    nodes: ['n1', 'n2', 'n3', 'hubWest', 'hubCore', 'hubEast', 'n9', 'n10'],
    duration: 13.8,
    delay: 0.3,
    tone: 'cool',
    size: 2.8,
  },
  {
    id: 'resourceRouteSouthArc',
    nodes: ['n11', 'n12', 'hubWest', 'hubCore', 'n15', 'n16', 'n17', 'hubEast', 'n18'],
    duration: 14.6,
    delay: 1.1,
    tone: 'warm',
    size: 2.6,
  },
  {
    id: 'resourceRouteCoreDrop',
    nodes: ['n5', 'hubCore', 'n15', 'n16', 'n17'],
    duration: 9.8,
    delay: 0.8,
    tone: 'cool',
    size: 2.4,
  },
  {
    id: 'resourceRouteWestReturn',
    nodes: ['n6', 'hubCore', 'hubWest', 'n14', 'n13', 'n12'],
    duration: 12.8,
    delay: 2.2,
    tone: 'warm',
    size: 2.3,
  },
  {
    id: 'resourceRouteHubSpine',
    nodes: ['n8', 'hubEast', 'hubCore', 'hubWest', 'n3'],
    duration: 11.2,
    delay: 3.1,
    tone: 'cool',
    size: 2.5,
  },
  {
    id: 'resourceRouteEastReturn',
    nodes: ['n19', 'n18', 'hubEast', 'hubCore', 'n4', 'n3', 'n2'],
    duration: 13.2,
    delay: 1.8,
    tone: 'warm',
    size: 2.4,
  },
];

const COLLABORATION_RESOURCE_PATHS = COLLABORATION_RESOURCE_ROUTES
  .map((route) => {
    const d = route.nodes
      .map((nodeId, nodeIndex) => {
        const node = COLLABORATION_NODE_LOOKUP[nodeId];
        if (!node) return null;
        return `${nodeIndex === 0 ? 'M' : 'L'} ${node.x} ${node.y}`;
      })
      .filter(Boolean)
      .join(' ');

    return d ? { ...route, d } : null;
  })
  .filter(Boolean);

const heroCopy = {
  en: {
    skipToThesis: 'Skip to Thesis',
    badgeDuration: '~30 min read',
    kicker: 'Origins • Exchange • Memory',
    titleLine1: 'The Great',
    titleLine2: 'Divergence',
    subtitleBefore: 'How humanity shifted from',
    subtitleHighlight1: 'collaborative gift economies',
    subtitleMiddle: 'to',
    subtitleHighlight2: 'competition-first systems',
    quote: 'For 90,000 years, humans lived in gift economies. Then everything changed.',
    beginJourney: 'Begin the Journey',
    beginJourneyAria: 'Begin the journey',
    viewSources: 'View Sources',
    viewSourcesAria: 'View research sources',
    keyboardHint: 'or use ← → keys to navigate',
    stat1Label: 'Historical Share',
    stat1Body: 'of human history was gift-based',
    stat2Label: 'Transition Start',
    stat2Body: 'years ago the crack opened',
    stat3Label: 'Market Adoption',
    stat3Body: 'market economy by 1990',
    meta: 'Based on research by Polly Wiessner, Marshall Sahlins, and Elinor Ostrom',
    scrollToContinue: 'Scroll to continue',
    nextSectionAria: 'Go to the next section',
    networkCaption: 'Collaboration networks building • resources in motion',
  },
  de: {
    skipToThesis: 'Zur These springen',
    badgeDuration: '~30 Min. Lesezeit',
    kicker: 'Ursprünge • Austausch • Erinnerung',
    titleLine1: 'Die große',
    titleLine2: 'Divergenz',
    subtitleBefore: 'Wie die Menschheit sich von',
    subtitleHighlight1: 'kollaborativen Geschenkökonomien',
    subtitleMiddle: 'zu',
    subtitleHighlight2: 'wettbewerbsorientierten Systemen entwickelte',
    quote: '90.000 Jahre lebten Menschen in Geschenkökonomien. Dann veränderte sich alles.',
    beginJourney: 'Reise starten',
    beginJourneyAria: 'Die Reise starten',
    viewSources: 'Quellen ansehen',
    viewSourcesAria: 'Forschungsquellen ansehen',
    keyboardHint: 'oder mit ← → navigieren',
    stat1Label: 'Historischer Anteil',
    stat1Body: 'der Menschheitsgeschichte war geschenkbasiert',
    stat2Label: 'Beginn des Übergangs',
    stat2Body: 'Jahren begann der Riss',
    stat3Label: 'Marktdurchsetzung',
    stat3Body: 'Marktökonomie bis 1990',
    meta: 'Basierend auf Forschung von Polly Wiessner, Marshall Sahlins und Elinor Ostrom',
    scrollToContinue: 'Zum Fortfahren scrollen',
    nextSectionAria: 'Zum nächsten Abschnitt',
    networkCaption: 'Kollaborationsnetzwerke bauen sich auf • Ressourcen in Bewegung',
  },
  es: {
    skipToThesis: 'Saltar a la tesis',
    badgeDuration: '~30 min de lectura',
    kicker: 'Origenes • Intercambio • Memoria',
    titleLine1: 'La Gran',
    titleLine2: 'Divergencia',
    subtitleBefore: 'Como la humanidad paso de',
    subtitleHighlight1: 'economias del don colaborativas',
    subtitleMiddle: 'a',
    subtitleHighlight2: 'sistemas centrados en la competencia',
    quote: 'Durante 90.000 anos, la humanidad vivio en economias del don. Luego todo cambio.',
    beginJourney: 'Comenzar el recorrido',
    beginJourneyAria: 'Comenzar el recorrido',
    viewSources: 'Ver fuentes',
    viewSourcesAria: 'Ver fuentes de investigacion',
    keyboardHint: 'o usa las teclas ← → para navegar',
    stat1Label: 'Cuota historica',
    stat1Body: 'de la historia humana se baso en el don',
    stat2Label: 'Inicio de la transicion',
    stat2Body: 'anos atras se abrio la grieta',
    stat3Label: 'Adopcion de mercado',
    stat3Body: 'economia de mercado en 1990',
    meta: 'Basado en investigaciones de Polly Wiessner, Marshall Sahlins y Elinor Ostrom',
    scrollToContinue: 'Desplazate para continuar',
    nextSectionAria: 'Ir a la siguiente seccion',
    networkCaption: 'Redes de colaboracion en formacion • recursos en movimiento',
  },
};

export default function HeroSlide({ refCallback, onNext }) {
  const { locale } = useLanguage();
  const copy = heroCopy[locale] || heroCopy.en;
  const shouldReduceMotion = useReducedMotion();
  const [isSmallScreen, setIsSmallScreen] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 767px)').matches : false
  );
  const [showKeyboardHint, setShowKeyboardHint] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(hover: hover) and (pointer: fine)').matches : false
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const smallScreenMedia = window.matchMedia('(max-width: 767px)');
    const keyboardHintMedia = window.matchMedia('(hover: hover) and (pointer: fine)');

    const update = () => {
      setIsSmallScreen(smallScreenMedia.matches);
      setShowKeyboardHint(keyboardHintMedia.matches);
    };

    update();

    smallScreenMedia.addEventListener('change', update);
    keyboardHintMedia.addEventListener('change', update);

    return () => {
      smallScreenMedia.removeEventListener('change', update);
      keyboardHintMedia.removeEventListener('change', update);
    };
  }, []);

  const theme = THEME;
  const particleCount = isSmallScreen ? theme.particleCount.mobile : theme.particleCount.desktop;
  const textureOpacity = isSmallScreen ? Math.max(0.16, theme.textureOpacity * 0.6) : theme.textureOpacity;
  const textureSize = isSmallScreen ? '24px 24px, 26px 26px' : '36px 36px, 40px 40px';

  const revealProps = (delay, y = 14, duration = 0.72) => (
    shouldReduceMotion
      ? {}
      : {
        initial: { opacity: 0, y },
        animate: { opacity: 1, y: 0 },
        transition: { delay, duration, ease: [0.22, 1, 0.36, 1] },
      }
  );

  const slowParallax = shouldReduceMotion
    ? {}
    : {
      animate: { x: [0, 8, 0], y: [0, -10, 0] },
      transition: { duration: 32, repeat: Infinity, ease: 'easeInOut' },
    };

  const mediumParallax = shouldReduceMotion
    ? {}
    : {
      animate: { x: [0, -12, 0], y: [0, 12, 0] },
      transition: { duration: 24, repeat: Infinity, ease: 'easeInOut' },
    };

  const collaborationNetworkMotion = shouldReduceMotion
    ? {}
    : {
      animate: { y: [0, -6, 0], opacity: [0.28, 0.38, 0.28] },
      transition: { duration: 18, repeat: Infinity, ease: 'easeInOut' },
    };

  const ctaShadowRest = `0 20px 30px -14px ${theme.primaryShadow}, 0 0 24px -16px rgba(143, 164, 224, 0.55)`;
  const ctaShadowHover = `0 26px 36px -14px ${theme.primaryShadow}, 0 0 40px -12px rgba(154, 180, 245, 0.75)`;
  const ctaShadowTap = `0 12px 20px -12px ${theme.primaryShadow}, 0 0 18px -14px rgba(143, 164, 224, 0.45)`;

  const moonGlow = 'radial-gradient(circle, rgba(164, 186, 255, 0.26) 0%, rgba(164, 186, 255, 0.06) 46%, transparent 72%)';
  const horizonGlow = 'linear-gradient(180deg, rgba(125, 156, 224, 0) 0%, rgba(125, 156, 224, 0.14) 55%, rgba(12, 18, 34, 0.7) 100%)';
  const statHaloBorder = 'rgba(213, 224, 255, 0.3)';
  const statHaloBg = 'rgba(213, 224, 255, 0.04)';

  const ctaButtonVariants = shouldReduceMotion
    ? {
      rest: { scale: 1, y: 0, boxShadow: ctaShadowRest },
      hover: { scale: 1, y: 0, boxShadow: ctaShadowRest },
      tap: { scale: 1, y: 0, boxShadow: ctaShadowRest },
    }
    : {
      rest: { scale: 1, y: 0, boxShadow: ctaShadowRest },
      hover: { scale: 1.03, y: -1, boxShadow: ctaShadowHover },
      tap: { scale: 0.98, y: 1, boxShadow: ctaShadowTap },
    };

  const ctaArrowVariants = shouldReduceMotion
    ? { rest: { x: 0 }, hover: { x: 0 }, tap: { x: 0 } }
    : { rest: { x: 0 }, hover: { x: 5 }, tap: { x: 2 } };

  return (
    <SlideWrapper id="hero" refCallback={refCallback} fullBleed={true} indigoTheme={false} className="relative min-h-[100svh] md:min-h-screen overflow-hidden bg-[#090604]">
      <div aria-hidden="true" className="absolute inset-0" style={{ backgroundImage: theme.background }} />
      <ParticleBackground connected={true} color={theme.particleColor} count={particleCount} />
      <a
        href="#thesis"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-30 focus:px-4 focus:py-2 focus:rounded-lg"
        style={{ backgroundColor: theme.skipBg, color: theme.skipText }}
      >
        {copy.skipToThesis}
      </a>
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{ backgroundImage: theme.glowOverlay }}
      />
      <motion.div
        aria-hidden="true"
        className="absolute -top-24 right-[6%] h-72 w-72 rounded-full"
        style={{ background: moonGlow, filter: 'blur(2px)' }}
        {...slowParallax}
      />
      <motion.div
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 h-44"
        style={{ background: horizonGlow }}
        {...mediumParallax}
      />
      <motion.svg
        aria-hidden="true"
        viewBox="0 0 1200 700"
        preserveAspectRatio="none"
        className="absolute inset-0 h-full w-full pointer-events-none"
        {...collaborationNetworkMotion}
      >
        <g fill="none" strokeLinecap="round">
          {COLLABORATION_EDGES.map(([sourceId, targetId], edgeIndex) => {
            const source = COLLABORATION_NODE_LOOKUP[sourceId];
            const target = COLLABORATION_NODE_LOOKUP[targetId];
            if (!source || !target) return null;
            const connectedToHub = source.tier === 'hub' || target.tier === 'hub';

            return (
              <motion.line
                key={`${sourceId}-${targetId}`}
                x1={source.x}
                y1={source.y}
                x2={target.x}
                y2={target.y}
                stroke={connectedToHub ? theme.networkLineStrong : theme.networkLine}
                strokeWidth={connectedToHub ? 1.3 : 1.05}
                initial={
                  shouldReduceMotion
                    ? false
                    : {
                      x2: source.x,
                      y2: source.y,
                      opacity: 0,
                    }
                }
                animate={
                  shouldReduceMotion
                    ? { opacity: connectedToHub ? 0.84 : 0.7 }
                    : {
                      x2: target.x,
                      y2: target.y,
                      opacity: connectedToHub ? 0.94 : 0.74,
                    }
                }
                transition={
                  shouldReduceMotion
                    ? { duration: 0 }
                    : {
                      duration: 1.15,
                      delay: edgeIndex * 0.035,
                      ease: [0.22, 1, 0.36, 1],
                    }
                }
              />
            );
          })}
        </g>
        {!shouldReduceMotion && (
          <g fill="none" strokeLinecap="round">
            {COLLABORATION_RESOURCE_PATHS.map((route) => (
              <g key={`${route.id}-trail`}>
                <path
                  id={route.id}
                  d={route.d}
                  stroke={theme.networkFlowTrail}
                  strokeWidth={1.05}
                  opacity={0.56}
                />
                <motion.path
                  d={route.d}
                  stroke={route.tone === 'warm' ? theme.networkFlowWarm : theme.networkFlowCool}
                  strokeWidth={1.45}
                  strokeDasharray="2 12"
                  fill="none"
                  initial={{ strokeDashoffset: 0 }}
                  animate={{ strokeDashoffset: [0, -96], opacity: [0.22, 0.6, 0.22] }}
                  transition={{
                    duration: route.duration,
                    repeat: Infinity,
                    ease: 'linear',
                    delay: route.delay,
                  }}
                />
              </g>
            ))}
          </g>
        )}
        {!shouldReduceMotion && (
          <g>
            {COLLABORATION_RESOURCE_PATHS.map((route) => {
              const resourceColor = route.tone === 'warm' ? theme.networkFlowWarm : theme.networkFlowCool;
              const followerDuration = Math.max(7.2, route.duration - 1.8);

              return (
                <g key={`${route.id}-resources`}>
                  <circle r={route.size} fill={resourceColor} opacity={0.95}>
                    <animateMotion dur={`${route.duration}s`} repeatCount="indefinite" begin={`${route.delay}s`}>
                      <mpath href={`#${route.id}`} xlinkHref={`#${route.id}`} />
                    </animateMotion>
                  </circle>
                  <circle r={Math.max(1.2, route.size - 1.15)} fill={theme.networkFlowSpark} opacity={0.82}>
                    <animateMotion
                      dur={`${followerDuration}s`}
                      repeatCount="indefinite"
                      begin={`${route.delay + route.duration * 0.42}s`}
                    >
                      <mpath href={`#${route.id}`} xlinkHref={`#${route.id}`} />
                    </animateMotion>
                  </circle>
                </g>
              );
            })}
          </g>
        )}
        <g>
          {COLLABORATION_NODES.filter((node) => node.tier === 'hub').map((node, index) => (
            <motion.circle
              key={`${node.id}-pulse`}
              cx={node.x}
              cy={node.y}
              r={12}
              fill={theme.networkPulse}
              animate={shouldReduceMotion ? undefined : { opacity: [0.08, 0.22, 0.08], r: [10, 15, 10] }}
              transition={
                shouldReduceMotion
                  ? undefined
                  : { duration: 5.4, repeat: Infinity, ease: 'easeInOut', delay: index * 0.7 }
              }
            />
          ))}
        </g>
        <g>
          {COLLABORATION_NODES.map((node) => (
            <circle
              key={node.id}
              cx={node.x}
              cy={node.y}
              r={COLLABORATION_NODE_RADIUS[node.tier]}
              fill={
                node.tier === 'hub'
                  ? theme.networkNodeHub
                  : node.tier === 'major'
                    ? theme.networkNodeMajor
                    : theme.networkNode
              }
            />
          ))}
        </g>
        <text
          x="600"
          y="632"
          textAnchor="middle"
          fontSize="14"
          style={{ fontFamily: '"Marcellus", serif', letterSpacing: '0.18em', textTransform: 'uppercase' }}
          fill={theme.networkLabel}
        >
          {copy.networkCaption}
        </text>
      </motion.svg>
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{ backgroundImage: theme.textureOverlay, opacity: textureOpacity, backgroundSize: textureSize }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{ backgroundImage: theme.vignetteOverlay }}
      />
      <div className="text-center relative z-10 max-w-6xl mx-auto px-6 sm:px-8 pt-12 md:pt-16 pb-24 md:pb-28" style={{ color: theme.text }}>
        <div>
          <motion.div
            className="inline-flex items-center gap-3 px-4 py-2 rounded-full border mb-8 backdrop-blur-sm shadow-lg shadow-black/30"
            style={{ backgroundColor: theme.badgeBg, borderColor: theme.badgeBorder }}
            {...revealProps(0.04, 10, 0.65)}
          >
            <Hourglass size={16} aria-hidden="true" style={{ color: theme.badgeIcon }} />
            <span className="text-sm font-semibold" style={{ color: theme.badgeText }}>A <AnimatedCounter end={100000} suffix=" years" /> journey</span>
            <span className="h-1 w-1 rounded-full" style={{ backgroundColor: theme.badgeIcon }} />
            <span className="text-xs font-medium" style={{ color: theme.badgeMuted }}>{copy.badgeDuration}</span>
          </motion.div>

          <motion.div className="flex items-center justify-center gap-3 mb-5" style={{ color: theme.kicker }} {...revealProps(0.12)}>
            <span className="h-px w-10 bg-gradient-to-r from-transparent" style={{ backgroundImage: `linear-gradient(90deg, transparent, ${theme.kickerLine})` }} />
            <span className="text-[10px] sm:text-xs uppercase tracking-[0.35em]">{copy.kicker}</span>
            <span className="h-px w-10 bg-gradient-to-l from-transparent" style={{ backgroundImage: `linear-gradient(270deg, transparent, ${theme.kickerLine})` }} />
          </motion.div>

          <motion.h1
            className="text-5xl sm:text-6xl md:text-7xl font-black mb-6"
            style={{ fontFamily: '"Cinzel Decorative", "Cinzel", serif', textShadow: '0 3px 18px rgba(0,0,0,0.5)', letterSpacing: '-0.02em', lineHeight: 0.96, textWrap: 'balance' }}
            {...revealProps(0.2, 16, 0.78)}
          >
            <span className="bg-clip-text text-transparent" style={{ backgroundImage: theme.headingGradient }}>
              {copy.titleLine1}
            </span>
            <br />
            <span style={{ color: theme.headingText }}>{copy.titleLine2}</span>
          </motion.h1>

          <motion.p
            className="text-xl md:text-2xl max-w-3xl mx-auto mb-4 leading-relaxed"
            style={{ fontFamily: '"Marcellus", serif', color: theme.bodyText, textWrap: 'pretty' }}
            {...revealProps(0.3, 16, 0.7)}
          >
            {copy.subtitleBefore}{' '}
            <span className="font-semibold" style={{ color: theme.accentPositive }}>{copy.subtitleHighlight1}</span>{' '}
            {copy.subtitleMiddle}{' '}
            <span className="font-semibold" style={{ color: theme.accentNegative }}>{copy.subtitleHighlight2}</span>
          </motion.p>

          <motion.p
            className="italic mb-12 text-lg"
            style={{ fontFamily: '"Marcellus", serif', color: theme.quoteText, textWrap: 'balance' }}
            {...revealProps(0.38, 14, 0.65)}
          >
            {copy.quote}
          </motion.p>

          <motion.div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16" {...revealProps(0.48, 18, 0.75)}>
            <motion.button
              initial="rest"
              animate="rest"
              whileHover="hover"
              whileTap="tap"
              variants={ctaButtonVariants}
              onClick={onNext}
              aria-label={copy.beginJourneyAria}
              className="px-8 py-4 font-bold rounded-2xl border text-lg focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-offset-2 active:translate-y-px"
              style={{
                backgroundImage: theme.primaryBg,
                color: theme.primaryText,
                borderColor: theme.primaryBorder,
                '--tw-ring-color': theme.focusRing,
                '--tw-ring-offset-color': theme.focusOffset,
              }}
            >
              <span>{copy.beginJourney} </span>
              <motion.span className="inline-block" variants={ctaArrowVariants} transition={{ type: 'spring', stiffness: 320, damping: 20 }}>→</motion.span>
            </motion.button>
            <a
              href="#sources"
              aria-label={copy.viewSourcesAria}
              className="font-medium underline underline-offset-4 transition focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-offset-2 rounded"
              style={{
                color: theme.secondaryText,
                textDecorationColor: theme.secondaryDecoration,
                '--tw-ring-color': theme.focusRing,
                '--tw-ring-offset-color': theme.focusOffset,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = theme.secondaryHover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = theme.secondaryText;
              }}
            >
              {copy.viewSources}
            </a>
            {showKeyboardHint && <span className="text-sm" style={{ color: theme.hintText }}>{copy.keyboardHint}</span>}
          </motion.div>

          <motion.div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 max-w-3xl mx-auto" {...revealProps(0.58, 20, 0.8)}>
            <div
              className="relative overflow-hidden rounded-2xl border py-4 px-3 backdrop-blur-sm shadow-lg"
              style={{ backgroundColor: theme.statBg, borderColor: theme.statBorder, boxShadow: `0 14px 26px -16px ${theme.statShadow}` }}
            >
              <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ backgroundImage: `linear-gradient(90deg, transparent, ${theme.statIcon1}, transparent)` }} />
              <div className="mx-auto mb-2 h-10 w-10 rounded-full flex items-center justify-center border" style={{ borderColor: statHaloBorder, backgroundColor: statHaloBg }}>
                <HandHeart size={20} aria-hidden="true" style={{ color: theme.statIcon1 }} />
              </div>
              <div className="text-[11px] uppercase tracking-[0.16em] mb-1" style={{ color: theme.kicker }}>{copy.stat1Label}</div>
              <div className="text-3xl md:text-4xl font-black" style={{ color: theme.statNum1 }}><AnimatedCounter end={90} suffix="%" /></div>
              <div className="text-xs mt-1" style={{ color: theme.statText }}>{copy.stat1Body}</div>
            </div>
            <div
              className="relative overflow-hidden rounded-2xl border py-4 px-3 backdrop-blur-sm shadow-lg"
              style={{ backgroundColor: theme.statBg, borderColor: theme.statBorder, boxShadow: `0 14px 26px -16px ${theme.statShadow}` }}
            >
              <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ backgroundImage: `linear-gradient(90deg, transparent, ${theme.statIcon2}, transparent)` }} />
              <div className="mx-auto mb-2 h-10 w-10 rounded-full flex items-center justify-center border" style={{ borderColor: statHaloBorder, backgroundColor: statHaloBg }}>
                <Wheat size={20} aria-hidden="true" style={{ color: theme.statIcon2 }} />
              </div>
              <div className="text-[11px] uppercase tracking-[0.16em] mb-1" style={{ color: theme.kicker }}>{copy.stat2Label}</div>
              <div className="text-3xl md:text-4xl font-black" style={{ color: theme.statNum2 }}><AnimatedCounter end={10} prefix="~" suffix="k" /></div>
              <div className="text-xs mt-1" style={{ color: theme.statText }}>{copy.stat2Body}</div>
            </div>
            <div
              className="relative overflow-hidden rounded-2xl border py-4 px-3 backdrop-blur-sm shadow-lg"
              style={{ backgroundColor: theme.statBg, borderColor: theme.statBorder, boxShadow: `0 14px 26px -16px ${theme.statShadow}` }}
            >
              <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ backgroundImage: `linear-gradient(90deg, transparent, ${theme.statIcon3}, transparent)` }} />
              <div className="mx-auto mb-2 h-10 w-10 rounded-full flex items-center justify-center border" style={{ borderColor: statHaloBorder, backgroundColor: statHaloBg }}>
                <Store size={20} aria-hidden="true" style={{ color: theme.statIcon3 }} />
              </div>
              <div className="text-[11px] uppercase tracking-[0.16em] mb-1" style={{ color: theme.kicker }}>{copy.stat3Label}</div>
              <div className="text-3xl md:text-4xl font-black" style={{ color: theme.statNum3 }}><AnimatedCounter end={98} suffix="%" /></div>
              <div className="text-xs mt-1" style={{ color: theme.statText }}>{copy.stat3Body}</div>
            </div>
          </motion.div>
          <motion.p className="text-xs mt-6 mb-8 md:mb-10" style={{ fontFamily: '"Marcellus", serif', color: theme.metaText }} {...revealProps(0.72, 10, 0.6)}>
            {copy.meta}
          </motion.p>
        </div>

        <motion.button
          type="button"
          onClick={onNext}
          aria-label={copy.nextSectionAria}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-offset-2 rounded-full flex flex-col items-center"
          style={{ '--tw-ring-color': theme.focusRing, '--tw-ring-offset-color': theme.focusOffset }}
        >
          <motion.span
            className="text-[11px] uppercase tracking-[0.18em] mb-1"
            style={{ color: theme.hintText }}
            animate={shouldReduceMotion ? undefined : { opacity: [0.6, 1, 0.6] }}
            transition={shouldReduceMotion ? undefined : { duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            {copy.scrollToContinue}
          </motion.span>
          <motion.span
            animate={shouldReduceMotion ? undefined : { y: [0, 10, 0] }}
            transition={shouldReduceMotion ? undefined : { repeat: Infinity, duration: 2, ease: 'easeInOut' }}
          >
            <ChevronDown size={32} aria-hidden="true" style={{ color: theme.chevron }} />
          </motion.span>
        </motion.button>
      </div>
    </SlideWrapper>
  );
}
